import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Provider, Booking, Category } from '../types';
import { categoriesAPI, bookingsAPI } from '../services/api';
import ProviderSelection from '../components/ProviderSelection';
import ScheduleSelection from '../components/ScheduleSelection';
import PaymentGateway from '../components/PaymentGateway';
import { ArrowLeft, ArrowRight, CheckCircle, Calendar, User, CreditCard } from 'lucide-react';

const NewBookingPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Booking flow state
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [notes, setNotes] = useState('');
  const [booking, setBooking] = useState<Booking | null>(null);

  // UI state
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    console.log('🎯 NewBookingPage loaded with category:', category);
    console.log('👤 User:', user?.email, 'Role:', user?.role);
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getCategories();
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const getCurrentCategory = () => {
    return categories?.find(cat => cat.slug === category);
  };

  const steps = [
    {
      number: 1,
      title: 'Select Provider',
      description: 'Choose your service provider',
      icon: User,
      completed: !!selectedProvider
    },
    {
      number: 2,
      title: 'Schedule',
      description: 'Pick date and time',
      icon: Calendar,
      completed: !!(selectedDate && selectedTimeSlot)
    },
    {
      number: 3,
      title: 'Payment',
      description: 'Complete your booking',
      icon: CreditCard,
      completed: !!booking
    }
  ];

  const canProceedToNext = () => {
    switch (currentStep) {
      case 1:
        return !!selectedProvider;
      case 2:
        return !!(selectedDate && selectedTimeSlot);
      case 3:
        return false; // Payment step handles its own completion
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (currentStep < 3 && canProceedToNext()) {
      if (currentStep === 2) {
        // Create booking before going to payment
        await createBooking();
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const createBooking = async () => {
    if (!selectedProvider || !selectedDate || !selectedTimeSlot || !user) {
      return;
    }

    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      const bookingData = {
        providerId: selectedProvider._id || selectedProvider.id,
        bookingDate: selectedDate,
        timeSlot: selectedTimeSlot,
        notes,
        serviceType: serviceType || getCurrentCategory()?.name || 'General Service'
      };

      const response = await bookingsAPI.createBooking(bookingData);
      setBooking(response.data.booking);
    } catch (error: any) {
      console.error('Error creating booking:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create booking';
      setError(errorMessage);
      
      // If it's a schedule availability error, go back to step 2 (time selection)
      if (errorMessage.includes('not available') || errorMessage.includes('schedule')) {
        setCurrentStep(2);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (transactionId: string) => {
    // Navigate to bookings page with success message
    navigate('/bookings', { 
      state: { 
        message: 'Booking confirmed successfully!',
        transactionId 
      }
    });
  };

  const handlePaymentError = (error: string) => {
    setError(error);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <ProviderSelection
            selectedProvider={selectedProvider}
            onSelectProvider={setSelectedProvider}
            category={category}
            skill={getCurrentCategory()?.slug}
          />
        );
      
      case 2:
        return selectedProvider ? (
          <div className="space-y-6">
            <ScheduleSelection
              provider={selectedProvider}
              selectedDate={selectedDate}
              selectedTimeSlot={selectedTimeSlot}
              onSelectDate={setSelectedDate}
              onSelectTimeSlot={setSelectedTimeSlot}
            />
            
            {/* Additional booking details */}
            <div className="space-y-4">
              <div>
                <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-2">
                  Service Type (Optional)
                </label>
                <input
                  type="text"
                  id="serviceType"
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                  placeholder={`e.g., ${getCurrentCategory()?.name || 'Describe your service needs'}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-black"
                />
              </div>
              
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any specific requirements or instructions..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-black"
                />
              </div>
            </div>
          </div>
        ) : null;
      
      case 3:
        return booking && selectedProvider ? (
          <PaymentGateway
            booking={booking}
            provider={selectedProvider}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
          />
        ) : (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-300">Preparing your booking...</p>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-300 hover:text-white"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back
              </button>
              <div className="ml-6">
                <h1 className="text-2xl font-bold text-white">
                  Book {getCurrentCategory()?.name || 'Service'}
                </h1>
                <p className="text-gray-600">
                  {getCurrentCategory()?.description || 'Complete your booking in 3 easy steps'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                      step.completed
                        ? 'bg-green-500 border-green-500 text-white'
                        : currentStep === step.number
                        ? 'bg-black border-gray-800 text-white'
                        : 'bg-white border-gray-300 text-gray-500'
                    }`}
                  >
                    {step.completed ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <step.icon className="h-5 w-5" />
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      step.completed || currentStep === step.number
                        ? 'text-gray-900'
                        : 'text-gray-500'
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-gray-500">{step.description}</p>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`hidden sm:block w-16 h-0.5 mx-6 ${
                    steps[index + 1].completed || currentStep > step.number
                      ? 'bg-green-500'
                      : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <p className="text-red-600">{error}</p>
              {error.includes('not available') && (
                <button
                  onClick={() => {
                    setError('');
                    setCurrentStep(2);
                  }}
                  className="ml-4 px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
                >
                  Select Different Time
                </button>
              )}
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-6">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        {currentStep < 3 && (
          <div className="mt-6 flex justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`px-6 py-2 rounded-lg font-medium ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Previous
            </button>

            <button
              onClick={handleNext}
              disabled={!canProceedToNext() || loading}
              className={`px-6 py-2 rounded-lg font-medium ${
                !canProceedToNext() || loading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-black text-white hover:bg-gray-800'
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Booking...
                </div>
              ) : (
                <div className="flex items-center">
                  {currentStep === 2 ? 'Create Booking' : 'Next'}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </div>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewBookingPage; 